import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, PencilSimple, X } from 'shared/icons';
import Button from '../../ui/Button';
import { MONO_CLASS } from '../../ui/Mono';
import { NormalizeScanCode, SCAN_METHOD_SCANNED, SCAN_METHOD_TYPED } from './collectorScan';
import ScanArmedBox from './ScanArmedBox';
import { useKiosk } from './KioskContext';

/**
 * A BOCA do coletor: um input OCULTO que fica sempre focado.
 *
 * ── Por que oculto ──────────────────────────────────────────────────────────
 * Coletor profissional é um teclado HID: ele "digita" o código lido e aperta
 * Enter. Não existe API de scanner no navegador — o que existe é foco. Se o
 * foco escapar (o operador encosta na tela, um botão rouba o foco, a tela
 * troca de passo), o próximo bipe cai no vazio e o operador fica bipando um
 * código que ninguém lê. Por isso o campo não é só "autofocus": ele se REPÕE
 * no foco em blur, em clique/toque na área do coletor, quando a aba volta a
 * ficar visível e a cada troca de passo (`focusKey`).
 *
 * `inputMode="none"` no campo oculto é o detalhe que faz isso ser usável no
 * celular: mantém o campo focado (recebendo as teclas do HID) SEM abrir o
 * teclado virtual em cima da tela. O teclado só aparece na digitação manual
 * do console, que é um campo visível e separado.
 *
 * ── Digitação manual: só no console ─────────────────────────────────────────
 * O PRD §8.2 pedia "fallback de digitação manual sempre"; por ordem do dono
 * (26/09) isso deixou de valer no QUIOSQUE: em /coletor e /bancada (`useKiosk`)
 * o terminal só BIPA — sem botão "Digitar", sem campo digitável. Produto sem
 * EAN dá erro e alguém cadastra o EAN; o chão não contorna digitando. No
 * console (/warehouse/*) a digitação continua disponível e o envio dela marca
 * `scan_method: TYPED` — é assim que a trilha distingue o que foi lido do que
 * foi digitado. Enquanto o campo manual está aberto, a reposição de foco do
 * campo oculto é suspensa: brigar pelo foco com quem está digitando seria pior
 * do que perder um bipe.
 */
const ScanInput = ({
    onScan,
    label,
    disabled = false,
    focusKey = '',
    hint = null,
    manualPlaceholder = '',
    className = '',
}) => {
    // Quiosque (/coletor, /bancada) = só bipe: a digitação manual não existe.
    const manualAllowed = !useKiosk();
    const hiddenRef = useRef(null);
    const manualRef = useRef(null);
    const [manualOpen, setManualOpen] = useState(false);
    const [manualValue, setManualValue] = useState('');
    const [buffer, setBuffer] = useState('');

    const manualShown = manualAllowed && manualOpen;
    const armed = !disabled && !manualShown;

    const focusHidden = useCallback(() => {
        if (!armed) {
            return;
        }
        const node = hiddenRef.current;
        if (node && document.activeElement !== node) {
            node.focus({ preventScroll: true });
        }
    }, [armed]);

    // Foco na montagem, a cada troca de passo (focusKey) e sempre que o campo
    // volta a ficar armado (fim da digitação manual, fim do envio).
    useEffect(() => {
        focusHidden();
    }, [focusHidden, focusKey]);

    // A aba/janela voltou: o navegador devolve o foco para onde estava ANTES de
    // sair, que raramente é o nosso campo.
    useEffect(() => {
        if (!armed) {
            return undefined;
        }
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                focusHidden();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('focus', handleVisibility);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('focus', handleVisibility);
        };
    }, [armed, focusHidden]);

    // Qualquer toque na página devolve o foco — inclusive o toque em um botão
    // (o clique dele já disparou; o `setTimeout(0)` roda depois do handler).
    useEffect(() => {
        if (!armed) {
            return undefined;
        }
        const handlePointer = () => {
            window.setTimeout(focusHidden, 0);
        };
        document.addEventListener('pointerup', handlePointer);
        return () => {
            document.removeEventListener('pointerup', handlePointer);
        };
    }, [armed, focusHidden]);

    const submit = (rawCode, scanMethod) => {
        const code = NormalizeScanCode(rawCode);
        if (!code) {
            return;
        }
        setBuffer('');
        setManualValue('');
        setManualOpen(false);
        onScan?.(code, scanMethod);
    };

    const handleHiddenKeyDown = (event) => {
        if (event.key !== 'Enter') {
            return;
        }
        event.preventDefault();
        submit(event.currentTarget.value, SCAN_METHOD_SCANNED);
        // Zera o campo mesmo se o pai não remontar a tela (bipe repetido).
        event.currentTarget.value = '';
    };

    const handleManualSubmit = (event) => {
        event.preventDefault();
        submit(manualValue, SCAN_METHOD_TYPED);
    };

    const openManual = () => {
        if (!manualAllowed) {
            return;
        }
        setManualOpen(true);
        setManualValue(buffer);
        window.setTimeout(() => manualRef.current?.focus(), 0);
    };

    const closeManual = () => {
        setManualOpen(false);
        setManualValue('');
        window.setTimeout(focusHidden, 0);
    };

    return (
        <div className={`space-y-3 ${className}`}>
            {/* Campo HID. Visualmente fora do caminho, mas focável e sem
                `aria-hidden` — esconder de leitor de tela um campo que recebe o
                foco é armadilha de acessibilidade. */}
            <input
                ref={hiddenRef}
                type="text"
                inputMode="none"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="characters"
                spellCheck={false}
                disabled={disabled}
                aria-label={label('wms.collector.hid_input_aria')}
                onChange={(event) => setBuffer(event.target.value)}
                onKeyDown={handleHiddenKeyDown}
                onBlur={() => window.setTimeout(focusHidden, 0)}
                className="pointer-events-none absolute h-px w-px opacity-0"
            />

            {manualShown ? (
                <form onSubmit={handleManualSubmit} className="space-y-3">
                    <label
                        htmlFor="collector-manual-input"
                        className="block text-base font-semibold text-black dark:text-white"
                    >
                        {label('wms.collector.manual_title')}
                    </label>
                    <input
                        id="collector-manual-input"
                        ref={manualRef}
                        type="text"
                        inputMode="text"
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="characters"
                        spellCheck={false}
                        value={manualValue}
                        onChange={(event) => setManualValue(event.target.value)}
                        placeholder={manualPlaceholder || label('wms.collector.manual_placeholder')}
                        className={`${MONO_CLASS} w-full rounded-xl border-2 border-mono-300 bg-white px-4 py-4 text-center text-2xl uppercase tracking-widest text-black placeholder-mono-300 focus:border-black focus:outline-none focus:ring-2 focus:ring-black dark:border-mono-700 dark:bg-mono-950 dark:text-white dark:placeholder-mono-700 dark:focus:border-white dark:focus:ring-white`}
                    />
                    <div className="flex gap-2">
                        <Button type="submit" size="lg" className="flex-1" disabled={!manualValue.trim()}>
                            <Check size={22} className="mr-2" />
                            {label('wms.collector.manual_confirm')}
                        </Button>
                        <Button type="button" variant="secondary" size="lg" onClick={closeManual}>
                            <X size={22} />
                            <span className="sr-only">{label('wms.common.cancel')}</span>
                        </Button>
                    </div>
                    <p className="text-center text-xs text-mono-500 dark:text-mono-400">
                        {label('wms.collector.manual_typed_note')}
                    </p>
                </form>
            ) : (
                <>
                    <ScanArmedBox armed={armed} buffer={buffer} label={label} />

                    {hint ? (
                        <p className="text-center text-sm text-mono-500 dark:text-mono-400">{hint}</p>
                    ) : null}

                    {/* No quiosque o terminal só bipa (ordem do dono, 26/09). */}
                    {manualAllowed ? (
                        <Button
                            type="button"
                            variant="secondary"
                            size="lg"
                            className="w-full"
                            onClick={openManual}
                            disabled={disabled}
                        >
                            <PencilSimple size={22} className="mr-2" />
                            {label('wms.collector.manual_open')}
                        </Button>
                    ) : null}
                </>
            )}
        </div>
    );
};

export default ScanInput;
